import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { clipTimelineQueryOptions, useClipCollection, type OlderPages } from '~/entities/exercise-clip';
import { useLoadedLiftSessions } from '~/entities/lift-history';
import { useSessionUser } from '~/entities/session';

import { buildGallery, type FormaGallery } from '../model/formaGallery';

import type { FormaState } from './useForma';

export interface FormaGalleryState {
  /** The read the shown gallery needs has no answer yet. */
  readonly pending: boolean;
  /** Every recording could not be read. One movement's failure is `view.missing` instead. */
  readonly failed: boolean;
  readonly retry: () => void;
  /** The next page of every recording; `null` while one movement narrows the gallery (never paged). */
  readonly older: OlderPages | null;
  readonly view: FormaGallery;
}

/**
 * „Nagrania": every recording from the trainer's collection narrowed to this client, paged — or, with
 * a movement chosen in the list, that movement's own timeline. Only the read the shown view needs is
 * enabled, so opening the gallery is one request, not one per filmed movement.
 */
export function useFormaGallery(
  clientId: number,
  list: FormaState['list'],
  exerciseId: number | null,
  open: boolean,
): FormaGalleryState {
  const { t, i18n } = useTranslation();
  const viewer = useSessionUser();
  const everything = open && exerciseId === null;
  const collection = useClipCollection(clientId, everything);
  const timeline = useQuery({
    ...clipTimelineQueryOptions(clientId, exerciseId ?? 0),
    enabled: open && exerciseId !== null,
  });
  const clips = collection.pages.flatMap((page) => page.clips);
  const sessions = useLoadedLiftSessions(
    clientId,
    exerciseId === null ? [...new Set(clips.map((clip) => clip.exerciseId))] : [exerciseId],
  );
  const name = list.all.find((row) => row.exerciseId === exerciseId)?.name ?? '';

  return {
    // A disabled query is `isPending` for ever, so each wait is gated on its own read being the one shown.
    pending: everything
      ? collection.pending || collection.stale
      : open && (timeline.isPending || (timeline.isError && timeline.isFetching)),
    failed: everything && collection.failed,
    retry: everything
      ? collection.retry
      : () => {
          void timeline.refetch();
        },
    older: exerciseId === null ? collection.older : null,
    view: buildGallery(
      {
        source:
          exerciseId === null
            ? {
                kind: 'client',
                clips,
                count: collection.clients?.find((client) => client.customerId === clientId)?.clipCount ?? null,
              }
            : { kind: 'movement', name, clips: timeline.data ?? (timeline.isError ? null : []) },
        sessions,
        viewerUserId: viewer?.id ?? null,
      },
      { t, locale: i18n.language },
    ),
  };
}
